import * as React from "react";

import * as R from "ramda";

import InputRange from "react-input-range";

enum YMWD {
  YEARS = "YEARS",
  MONTHS = "MONTHS",
  WEEKS = "WEEKS",
  DAYS = "DAYS"
}

const YMWD_CONVERT_TO_DAYS = {
  YEARS: 365,
  MONTHS: 30,
  WEEKS: 7,
  DAYS: 1,
} as const;

const YMWD_MAXIMUM_TIME_UNIT_VALUE = {
  YEARS: 110,
  MONTHS: 24,
  WEEKS: 24,
  DAYS: 30,
} as const;

const DEFAULT_STATE = {
  timeUnit: YMWD.YEARS,
  internalTimeUnitValue: {
    min: 0,
    max: YMWD_MAXIMUM_TIME_UNIT_VALUE[YMWD.YEARS],
  },
};

interface GenYMWDFilterValue {
  min: number;
  max: number;
}

function divideRange(range: GenYMWDFilterValue, integer: number) {
  return {
    min: range.min/integer,
    max: range.max/integer,
  };
}

function multiplyRange(range: GenYMWDFilterValue, integer: number) {
  return {
    min: range.min * integer,
    max: range.max * integer,
  };
}

interface GenYMWDFilterProps extends ConnectedListFilterInterface {
  displayLikeFilterGroup?: boolean;
  title: string;
  filterKey: string;
  filterColumn?: string;
}

interface GenYMWDFilterState {
  timeUnit: YMWD;
  internalTimeUnitValue: GenYMWDFilterValue;
}

export default class GenYMWDFilter extends React.Component<GenYMWDFilterProps, GenYMWDFilterState> {
  constructor(props: GenYMWDFilterProps) {
    super(props);

    const value = this.value();

    if (value?.max) {
      const timeUnits = Object.values(YMWD).reverse();
      const timeUnit = timeUnits.find((timeUnit) => {
        const unitDays = YMWD_CONVERT_TO_DAYS[timeUnit];
        const unitMax = YMWD_MAXIMUM_TIME_UNIT_VALUE[timeUnit];
        const maxDays = unitDays * unitMax;

        return (value.max <= maxDays) && (value.max % unitDays === 0);
      });

      this.state = timeUnit ? {
        timeUnit: timeUnit,
        internalTimeUnitValue: divideRange(value, YMWD_CONVERT_TO_DAYS[timeUnit]),
      } : DEFAULT_STATE;
    } else {
      this.state = DEFAULT_STATE;
    }
  }

  value() {
    const {
      activeFilters={},
      filterKey,
    } = this.props;

    const activeFilter = activeFilters[filterKey];
    return activeFilter ? getValueFromFilterWhereQuery(activeFilter) : undefined;
  }

  toggleFilter() {
    const {
      filterKey,
      removeListFilter,
      resetListPages,
      readListPage,
    } = this.props;

    if (this.value()) {
      removeListFilter(filterKey);
      resetListPages();
      readListPage();
    } else {
      this.resetFilter();
    }
  }

  resetFilter() {
    this.setState(DEFAULT_STATE, this.syncExternal);
  }

  handleTimeUnitChange(event: React.ChangeEvent<HTMLSelectElement>) {
    const timeUnit = event.target.value as YMWD;
    const newTimeRange = {
      min: 0,
      max: YMWD_MAXIMUM_TIME_UNIT_VALUE[timeUnit],
    };

    this.setState({
      timeUnit: event.target.value as YMWD,
      internalTimeUnitValue: newTimeRange,
    }, this.syncExternal);
  }

  handleInternalChange(internalTimeUnitValue: GenYMWDFilterValue) {
    this.setState({internalTimeUnitValue: internalTimeUnitValue});
  }

  handleExternalChange(internalTimeUnitValue: GenYMWDFilterValue) {
    this.setState({internalTimeUnitValue: internalTimeUnitValue}, this.syncExternal);
  }

  syncExternal() {
    const {
      filterKey,
      filterColumn,
      addListFilter,
      resetListPages,
      readListPage,
    } = this.props;

    const newFilter = generateFilterWhereQueryFromValues(filterKey, this.convertDisplayRangeToDayRange(this.state.internalTimeUnitValue), filterColumn);

    if (newFilter) {
      addListFilter(filterKey, newFilter);
      resetListPages();
      readListPage();
    }
  }

  convertDisplayRangeToDayRange(displayRange: GenYMWDFilterValue) {
    const range = multiplyRange(displayRange, YMWD_CONVERT_TO_DAYS[this.state.timeUnit]);
    range.min = Math.max(1, range.min);
    return range;
  }

  render() {
    let classes = "gen-list-filter-2 gen-list-ymwd-filter";
    let title = <h5>{this.props.title}</h5>;

    if (this.props.displayLikeFilterGroup) {
      classes += " like-checkbox-filters";
      title = <h4>{this.props.title}</h4>;
    }

    const value = this.value();

    return (
      <div className={classes}>
        <div className="gen-list-filter-title">
          {title}
          <div className="cursor-pointer"><span onClick={this.toggleFilter.bind(this)}>{value ? "All" : "Filter"}</span></div>
        </div>
        {value && (
          <div className="gen-list-filter-body">
            <div style={{marginTop: "5px"}}>
              <select value={this.state.timeUnit} onChange={this.handleTimeUnitChange.bind(this)} style={{width: "100%"}}>
                {Object.values(YMWD).map((unit) => <option key={unit} value={unit}>{unit.charAt(0).toUpperCase() + unit.slice(1).toLowerCase()}</option>)}
              </select>
            </div>
            <div style={{margin: "20px 10px"}}>
              <InputRange
                minValue={0}
                maxValue={YMWD_MAXIMUM_TIME_UNIT_VALUE[this.state.timeUnit]}
                value={this.state.internalTimeUnitValue}
                onChange={this.handleInternalChange.bind(this) as (v: GenYMWDFilterValue | number) => void} // must include '| number' to satisfy InputRange type defs
                onChangeComplete={this.handleExternalChange.bind(this) as (v: GenYMWDFilterValue | number) => void}
              />
            </div>
          </div>
        )}
      </div>
    );
  }
}

function generateFilterWhereQueryFromValues(filterKey: string, value?: GenYMWDFilterValue, filterColumn?: string): Filter | undefined {
  if (value) {
    const mdataColumn = filterColumn || "mdata";
    const mdataKey = `${mdataColumn}->>${filterKey}::int`;
    return ["and", [">=", mdataKey, value.min], ["<=", mdataKey, value.max]];
  }

  return undefined;
}

function getValueFromFilterWhereQuery(filterWhereQuery: Filter): {min: number, max: number} | undefined {
  switch (filterWhereQuery[0]) {
    case "and":
      if (filterWhereQuery[2]) {
        return {min: R.last(filterWhereQuery[1]), max: R.last(filterWhereQuery[2])};
      }

      return undefined;
    default:
      return undefined;
  }
}
